import {
  Box,
  Button,
  Container,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Modal,
  PasswordInput,
} from "@mantine/core";
import TitleComponent from "@/components/shared/TitleComponent";
import { IconLock, IconLogout, IconMail, IconPhone, IconClock, IconRefresh, IconTrash } from "@tabler/icons-react";
import { useUser } from "@/context/UserContext";
import { useState } from "react";
import { successNotification } from "@/shared/utils/successNotification";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

export default function SettingsPage() {
  const { logout } = useUser();
  const [resetPasswordOpened, setResetPasswordOpened] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const validatePasswords = () => {
    const newErrors = {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    };

    let isValid = true;

    if (!oldPassword) {
      newErrors.oldPassword = "Old password is required";
      isValid = false;
    }

    if (!newPassword) {
      newErrors.newPassword = "New password is required";
      isValid = false;
    } else if (newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
      isValid = false;
    } else if (oldPassword === newPassword) {
      newErrors.newPassword = "New password must be different from old password";
      isValid = false;
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleResetPassword = async () => {
    if (!validatePasswords()) {
      return;
    }

    setLoading(true);
    try {
      await trpc.auth.changePassword.mutate({
        oldPassword,
        newPassword,
      });
      successNotification({
        title: "Success",
        message: "Password changed successfully",
      });
      setResetPasswordOpened(false);
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setErrors({ oldPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      handleTrpcError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setResetPasswordOpened(false);
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrors({ oldPassword: "", newPassword: "", confirmPassword: "" });
  };

  return (
    <Box mih="100vh" pb={40}>
      <TitleComponent title="Settings" />

      <Container fluid px="lg" py="xl">
        <Stack gap="xl">
          {/* Top Row: Account Actions */}
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            {/* Reset Password */}
            <Paper p="md" radius="md" withBorder>
              <Stack justify="space-between" h="100%" gap="md">
                <Group align="flex-start" gap="md" wrap="nowrap">
                  <IconLock size={28} style={{ marginTop: 4 }} color="var(--mantine-color-gray-8)" />
                  <Stack gap={4}>
                    <Text fw={500} size="lg" c="slate.7">
                      Reset Password
                    </Text>
                    <Text size="sm" c="slate.5" lh={1.5}>
                      Want to change your password? Use a strong mix of letters, numbers, and symbols for better
                      security.
                    </Text>
                  </Stack>
                </Group>
                <Group justify="flex-end" gap="md">
                  <Button
                    variant="subtle"
                    color="primary.7"
                    leftSection={<IconRefresh size={16} />}
                    onClick={() => setResetPasswordOpened(true)}
                  >
                    Reset Password
                  </Button>
                </Group>
              </Stack>
            </Paper>

            {/* Logout */}
            <Paper p="md" radius="md" withBorder>
              <Stack justify="space-between" h="100%" gap="xs">
                <Group align="flex-start" gap="md" wrap="nowrap">
                  <IconLogout size={24} style={{ marginTop: 4 }} color="var(--mantine-color-gray-8)" />
                  <Stack gap={4}>
                    <Text fw={500} size="lg">
                      Logout
                    </Text>
                    <Text size="sm" c="slate.5" lh={1.5}>
                      You'll be signed out from this device. Log in again to access your account.
                    </Text>
                  </Stack>
                </Group>
                <Group justify="flex-end">
                  <Button variant="subtle" color="primary.7" onClick={logout}>
                    Logout
                  </Button>
                </Group>
              </Stack>
            </Paper>
            {/* Delete Account */}
            <Paper p="md" radius="md" withBorder>
              <Stack justify="space-between" h="100%" gap="xs">
                <Group align="flex-start" gap="md" wrap="nowrap">
                  <IconTrash size={24} style={{ marginTop: 4 }} color="var(--mantine-color-gray-8)" />
                  <Stack gap={4}>
                    <Text fw={500} size="lg">
                      Delete Account
                    </Text>
                    <Text size="sm" c="slate.5" lh={1.5}>
                      Want to delete your account? Please contact our support team and we'll assist you.
                    </Text>
                  </Stack>
                </Group>
              </Stack>
            </Paper>
          </SimpleGrid>
          {/* Support Section */}
          <Stack gap="md">
            <Divider />
            <Title order={3} fw={500} fz={24}>
              Support
            </Title>
            <Stack gap={4}>
              <Text size="md" fz={18}>
                Need help with the app or anything else? Feel free to reach out using the contact details below.
              </Text>
              <Text size="sm" c="slate.5">
                Our team may take a little time to respond if they're assisting other users—thanks for your patience!
              </Text>
            </Stack>

            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" mt="md">
              {/* Email */}
              <Paper p="xl" radius="md" withBorder>
                <Group align="flex-start" gap="lg">
                  <ThemeIcon size="xl" radius="md" variant="light">
                    <IconMail size={24} />
                  </ThemeIcon>
                  <Stack gap={2}>
                    <Text fw={400}>Email</Text>
                    <Text size="sm" c="slate.6">
                      support@gtconnect.com
                    </Text>
                    <Text size="xs" c="slate.6" mt={4}>
                      We'll respond within 24 hours
                    </Text>
                  </Stack>
                </Group>
              </Paper>

              {/* Phone */}
              <Paper p="xl" radius="md" withBorder>
                <Group align="flex-start" gap="lg">
                  <ThemeIcon size="xl" radius="md" variant="light">
                    <IconPhone size={24} />
                  </ThemeIcon>
                  <Stack gap={2}>
                    <Text fw={400}>Phone</Text>
                    <Text size="sm" c="slate.6">
                      +1 (555) 123-4567
                    </Text>
                    <Text size="xs" c="slate.6" mt={4}>
                      Mon-Fri, 9am-6pm EST
                    </Text>
                  </Stack>
                </Group>
              </Paper>

              {/* Business Hours */}
              <Paper p="xl" radius="md" withBorder>
                <Group align="flex-start" gap="lg">
                  <ThemeIcon size="xl" radius="md" variant="light">
                    <IconClock size={24} />
                  </ThemeIcon>
                  <Stack gap={2}>
                    <Text fw={400}>Business Hours</Text>
                    <Text size="sm" c="slate.6">
                      Monday - Friday
                    </Text>
                    <Text size="xs" c="slate.6" mt={4}>
                      9:00 AM - 6:00 PM EST
                    </Text>
                  </Stack>
                </Group>
              </Paper>
            </SimpleGrid>
          </Stack>
        </Stack>
      </Container>

      {/* Reset Password Modal */}
      <Modal opened={resetPasswordOpened} onClose={handleModalClose} title="Reset Password" centered size="md">
        <Stack gap="lg">
          <PasswordInput
            label="Old Password"
            placeholder="Enter your current password"
            value={oldPassword}
            onChange={(e) => {
              setOldPassword(e.target.value);
              setErrors({ ...errors, oldPassword: "" });
            }}
            error={errors.oldPassword}
            required
          />

          <PasswordInput
            label="New Password"
            placeholder="Enter new password (min 8 characters)"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setErrors({ ...errors, newPassword: "" });
            }}
            error={errors.newPassword}
            required
          />

          <PasswordInput
            label="Confirm New Password"
            placeholder="Re-enter new password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setErrors({ ...errors, confirmPassword: "" });
            }}
            error={errors.confirmPassword}
            required
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" color="gray" onClick={handleModalClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} loading={loading}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
